import React from "react";
import { Link } from "react-router-dom";
import AdminPageShell from "./AdminPageShell";
import useFetch from "../../useFetch";

const statCards = [
  { id: "employers", label: "Employers", icon: "la la-building" },
  { id: "candidates", label: "Candidates", icon: "la la-users", className: "ui-red" },
  { id: "active_jobs", label: "Active Jobs", icon: "la la-briefcase", className: "ui-yellow" },
  { id: "pending_review_jobs", label: "Pending Review", icon: "la la-clipboard-check", className: "ui-green" },
  { id: "applications", label: "Applications", icon: "la la-file-invoice", className: "ui-green" },
  { id: "blocked_users", label: "Blocked Users", icon: "la la-user-lock", className: "ui-red" },
  { id: "closed_jobs", label: "Closed Jobs", icon: "la la-ban", className: "ui-yellow" },
  { id: "expired_jobs", label: "Expired Jobs", icon: "la la-calendar-times", className: "ui-red" },
];

const formatDate = (date) => date ? new Date(date).toLocaleDateString() : "Open";
const statusLabels = {
  pending_admin_review: "Pending Review",
  pending_verification: "Pending Verification",
};
const getJobStatusClass = (status) => {
  if (["closed", "declined", "expired"].includes(status)) return "danger";
  if (["pending_admin_review", "pending_verification", "paused"].includes(status)) return "warning";

  return "";
};

const AdminDashboard = () => {
  const { response, isLoading, error } = useFetch(process.env.REACT_APP_BASE_API_URL + "/admin/dashboard");
  const dashboard = response?.dashboard;
  const stats = dashboard?.stats ?? {};
  const recentJobs = dashboard?.recent_jobs ?? [];
  const recentApplications = dashboard?.recent_applications ?? [];
  const latestPayments = dashboard?.latest_payments ?? [];
  const auditLogs = dashboard?.recent_audit_logs ?? [];

  return (
    <AdminPageShell
      active="dashboard"
      title="Super Admin Dashboard"
      text="Monitor jobs, employers, payments, and applications from one place."
    >
      <div className="row">
        {statCards.map((item) => (
          <div className="col-xl-3 col-lg-6 col-md-6 col-sm-12" key={item.id}>
            <div className={`ui-item ${item.className || ""}`}>
              <div className="left"><i className={`icon ${item.icon}`}></i></div>
              <div className="right">
                <h4>{stats[item.id] ?? 0}</h4>
                <p>{item.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {error && <div className="alert alert-danger"><strong>{error}</strong></div>}
      {isLoading && <h5 className="text-center my-3">Loading Data</h5>}

      <div className="row">
        <div className="col-lg-7">
          <div className="ls-widget">
            <div className="widget-title">
              <h4>Recent Jobs</h4>
              <Link to="/admin-jobs" className="theme-btn btn-style-three">Manage Jobs</Link>
            </div>
            <div className="widget-content">
              <div className="admin-simple-list">
                {recentJobs.slice(0, 5).map((job) => (
                  <div className="admin-list-item" key={job.id}>
                    <div>
                      <strong>{job.title}</strong>
                      <span>{job.company_name} - {job.applications_count} applicants - {formatDate(job.application_deadline)}</span>
                    </div>
                    <span className={getJobStatusClass(job.status)}>{statusLabels[job.status] ?? job.status}</span>
                  </div>
                ))}
                {!isLoading && recentJobs.length === 0 && <div className="admin-empty-state">No jobs found.</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-5">
          <div className="ls-widget">
            <div className="widget-title">
              <h4>Admin Activity</h4>
            </div>
            <div className="widget-content">
              <div className="admin-simple-list">
                {recentApplications.map((item) => (
                  <div className="admin-list-item" key={item.id}>
                    <div>
                      <strong>{item.candidate_name} applied for {item.job_title}</strong>
                      <span>{item.candidate_email}</span>
                    </div>
                    <span className={item.status === "rejected" ? "danger" : item.status === "shortlisted" ? "warning" : ""}>{item.status}</span>
                  </div>
                ))}
                {!isLoading && recentApplications.length === 0 && <div className="admin-empty-state">No recent applications.</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="ls-widget">
            <div className="widget-title">
              <h4>Recent Audit Logs</h4>
              <Link to="/admin-audit-logs" className="theme-btn btn-style-three">View All</Link>
            </div>
            <div className="widget-content">
              <div className="admin-simple-list">
                {auditLogs.slice(0, 6).map((item) => (
                  <div className="admin-list-item" key={item.id}>
                    <div>
                      <strong>{item.label}</strong>
                      <span>{item.target_label || "Platform"} - {formatDate(item.created_at)}</span>
                    </div>
                    <span>{item.admin_name || "Admin"}</span>
                  </div>
                ))}
                {!isLoading && auditLogs.length === 0 && <div className="admin-empty-state">No audit logs yet.</div>}
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-12">
          <div className="ls-widget">
            <div className="widget-title">
              <h4>Latest Payments</h4>
              <Link to="/admin-payments" className="theme-btn btn-style-three">View Payments</Link>
            </div>
            <div className="widget-content">
              <div className="admin-table-list">
                {latestPayments.slice(0, 5).map((payment) => (
                  <div className="admin-table-row" key={payment.id}>
                    <div>
                      <strong>{payment.invoice_number}</strong>
                      <span>{payment.item_name} - {payment.currency} {payment.total} - {formatDate(payment.paid_at || payment.created_at)}</span>
                    </div>
                    <span className={payment.status === "failed" ? "danger" : ""}>{payment.status}</span>
                  </div>
                ))}
                {!isLoading && latestPayments.length === 0 && <div className="admin-empty-state">No payments found.</div>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminPageShell>
  );
};

export default AdminDashboard;
